import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { loginUser } from "../../../core/api/authService";
import { login } from "../../../core/data/redux/authSlice";
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { setPermissions } from "../../../core/data/redux/permissionSlice";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../../../environment";
import {
  getAllPermissions,
  PermissionRecord,
} from "../../../core/api/permissionMatrix";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [passwordVisibility, setPasswordVisibility] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const storedPermissions = localStorage.getItem("authPermissions");

    if (token) {
      try {
        const decoded: any = jwtDecode(token);
        dispatch(
          login({
            token,
            role: decoded.role,
            user: decoded.user,
            sub: decoded.sub,
            employee_id: decoded.id,
            department: decoded.department,
            permissions: storedPermissions
              ? JSON.parse(storedPermissions)
              : null,
          })
        );
      } catch (err) {
        console.error("Invalid token, clearing it");
        localStorage.removeItem("authToken");
      }
    }
  }, []);

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
    rememberMe: Yup.boolean(),
  });

  const handleLogin = async (values: {
    email: string;
    password: string;
    rememberMe: boolean;
  }) => {
    try {
      const data = await loginUser(
        values.email,
        values.password,
        values.rememberMe
      );

      if (data.access_token) {
        const decodedToken: any = jwtDecode(data.access_token);
        const userRole = decodedToken.role || "user";

        localStorage.setItem("authToken", data.access_token);
        localStorage.setItem("userRole", userRole);

        // 🔐 USE TOKEN PERMISSIONS (not API permissions)
        // The token already contains the user's actual permissions
        const tokenPermissions = decodedToken.permissions || {};

        // Optional: Fetch all permissions only after login with valid token
        let rawPermissions: PermissionRecord[] = [];
        try {
          rawPermissions = await getAllPermissions(data.access_token);
          dispatch(setPermissions(rawPermissions)); // This is for other uses
        } catch (err) {
          console.error("Permission fetch error:", err);
          // Don't show error for this, continue with token permissions
        }

        // Store token permissions for sidebar use
        localStorage.setItem(
          "authPermissions",
          JSON.stringify(tokenPermissions)
        );

        // 🧠 Store user session in Redux with TOKEN permissions
        dispatch(
          login({
            token: data.access_token,
            role: userRole,
            user: decodedToken.user,
            sub: decodedToken.sub,
            employee_id: decodedToken.id,
            department: decodedToken.department,
            permissions: tokenPermissions, // ✅ Use token permissions
          })
        );

        // ⏳ Set token expiry auto-logout
        const expiryTime = decodedToken.exp * 1000;
        const timeUntilExpiry = expiryTime - Date.now();
        localStorage.setItem("token_expiry", expiryTime.toString());

        setTimeout(() => {
          localStorage.removeItem("authToken");
          localStorage.removeItem("token_expiry");
          dispatch(
            login({
              token: "",
              role: "",
              user: "",
              sub: "",
              employee_id: "",
              department: "",
              permissions: {}, // logout fallback
            })
          );
          toast.info("Session expired. Please log in again.");
          navigate("/login");
        }, timeUntilExpiry);

        // Redirect by role
        if (userRole === "Superadmin") {
          navigate(all_routes.superAdminDashboard);
        } else if (["Admin", "Manager"].includes(userRole)) {
          navigate(all_routes.adminDashboard);
        } else {
          navigate(all_routes.employeeDashboard);
        }
      } else {
        toast.error("Invalid credentials");
      }
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  const formik = useFormik({
    initialValues: { email: "", password: "", rememberMe: false },
    validationSchema,
    onSubmit: handleLogin,
  });

  return (
    <div className="container-fluid">
      <div className="w-100 vh-100 d-flex align-items-center justify-content-center">
        <div className="col-md-6 col-lg-4 p-4 border rounded shadow bg-white">
          <div className="text-center mb-4">
            <ImageWithBasePath
              src="assets/img/logo.svg"
              className="img-fluid"
              alt="Logo"
            />
          </div>
          <h2 className="text-center">Sign In</h2>
          <p className="text-center text-muted">
            Please enter your details to sign in
          </p>

          <form onSubmit={formik.handleSubmit}>
            <div className="mb-3">
              <label className="form-label">Email Address</label>
              <input
                type="email"
                className="form-control"
                {...formik.getFieldProps("email")}
              />
              {formik.touched.email && formik.errors.email && (
                <small className="text-danger">{formik.errors.email}</small>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label">Password</label>
              <div className="input-group">
                <input
                  type={passwordVisibility ? "text" : "password"}
                  className="form-control"
                  {...formik.getFieldProps("password")}
                />
                <span
                  className="input-group-text"
                  onClick={() => setPasswordVisibility(!passwordVisibility)}
                  style={{ cursor: "pointer" }}
                >
                  <i
                    className={
                      passwordVisibility ? "ti ti-eye" : "ti ti-eye-off"
                    }
                  />
                </span>
              </div>
              {formik.touched.password && formik.errors.password && (
                <small className="text-danger">{formik.errors.password}</small>
              )}
            </div>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="rememberMe"
                  {...formik.getFieldProps("rememberMe")}
                />
                <label className="form-check-label" htmlFor="rememberMe">
                  Remember Me
                </label>
              </div>
              <a href={all_routes.forgotPassword} className="text-danger">
                Forgot Password?
              </a>
            </div>

            <button type="submit" className="btn btn-primary w-100">
              Sign In
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
export default Login;